import { fork, put, all, call, takeLatest } from "redux-saga/effects";

import {
  getMarginRecordsSuccess,
  getMarginRecordsFailure,
  createMarginRecordSuccess,
  createMarginRecordFailure,
  updateMarginRecordSuccess,
  updateMarginRecordFailure,
  deleteMarginRecordSuccess,
  deleteMarginRecordFailure,
  getMarginSummarySuccess,
  getMarginSummaryFailure,
} from "./marginSlice";

import {
  getMarginRecords as getMarginRecordsApi,
  createMarginRecord as createMarginRecordApi,
  updateMarginRecord as updateMarginRecordApi,
  deleteMarginRecord as deleteMarginRecordApi,
  getMarginSummary as getMarginSummaryApi,
} from "../../helpers/auth_aws_helper";

// 🛠️ Utility function to handle error responses consistently
const handleApiError = (error, operation) => {
  console.error(`Saga: ${operation} Error =`, error);
  
  if (error.response) {
    // Server responded with error status
    const errorData = error.response.data;
    
    // Handle API Gateway specific errors
    if (errorData?.message) {
      // API Gateway timeout or other gateway errors
      if (errorData.message.includes("timeout") || errorData.message.includes("timed out")) {
        return "Request timed out. Please try again.";
      }
      if (errorData.message.includes("Endpoint request")) {
        return "Service temporarily unavailable. Please try again later.";
      }
      return errorData.message;
    }
    
    // Handle other error formats
    if (typeof errorData === "string") {
      return errorData;
    }
    if (errorData?.error) {
      return errorData.error;
    }
    
    return `Server error: ${error.response.status}`;
  } else if (error.request) {
    // Request was made but no response received (timeout, network error, etc.)
    if (error.message && error.message.includes("timeout")) {
      return "Request timed out. Please check your connection and try again.";
    }
    return "No response received from server. Please check your connection.";
  } else {
    // Something else happened
    return error.message || "An unexpected error occurred.";
  }
};

// 🔄 Worker: Fetch Margin Records
function* fetchMarginRecords({ payload: requestingOrgCode }) {
  try {
    console.log("Saga: Calling getMarginRecords with requestingOrgCode =", requestingOrgCode);
    const response = yield call(getMarginRecordsApi, requestingOrgCode);
    console.log("Saga: Margin Records Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      // The response structure is { ...response.data, status: response.status }
      // If response.data is an array, it gets spread into the response object
      // We need to convert it back to an array
      const { status, ...rest } = response;
      console.log("Saga: Rest after destructuring =", rest);
      
      // If the API returns an array directly, it should be in response.data
      // But if it gets spread, we need to reconstruct it
      let marginRecords;
      if (Array.isArray(response.data)) {
        marginRecords = response.data;
      } else if (Array.isArray(rest)) {
        marginRecords = rest;
      } else {
        marginRecords = Object.values(rest);
      }
      
      console.log("Saga: Final margin records =", marginRecords);
      yield put(getMarginRecordsSuccess(marginRecords));
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(getMarginRecordsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Margin Records");
    yield put(getMarginRecordsFailure(errorMessage));
  }
}

// 📄 Worker: Create Margin Record
function* createMarginRecordSaga({ payload }) {
  try {
    console.log("Saga: Calling createMarginRecord with payload =", payload);
    const response = yield call(createMarginRecordApi, payload);
    console.log("Saga: Create Margin Record Response =", response);

    if (response.status === 200) {
      // Handle both object and string responses
      const successData = typeof response.data === 'string' ? { message: response.data } : response.data;
      yield put(createMarginRecordSuccess(successData));
      // Automatically refresh margin records after successful creation
      if (payload.requestingOrgCode) {
        yield put({ type: "margin/getMarginRecords", payload: payload.requestingOrgCode });
      }
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(createMarginRecordFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Create Margin Record");
    yield put(createMarginRecordFailure(errorMessage));
  }
}

// 📝 Worker: Update Margin Record
function* updateMarginRecordSaga({ payload }) {
  try {
    console.log("Saga: Calling updateMarginRecord with payload =", payload);
    const response = yield call(updateMarginRecordApi, payload);
    console.log("Saga: Update Margin Record Response =", response);

    if (response.status === 200) {
      // Remove status from response
      const { status, ...rest } = response;

      // If rest looks like a spread string (all numeric keys), rebuild the string
      const isStringObject =
        Object.keys(rest).every((key) => !isNaN(key)) &&
        Object.values(rest).every((val) => typeof val === "string");

      const finalResult = isStringObject ? Object.values(rest).join("") : rest;
      const successData = { message: finalResult };

      yield put(updateMarginRecordSuccess(successData));
      // Automatically refresh margin records after successful update
      if (payload.requestingOrgCode) {
        yield put({ type: "margin/getMarginRecords", payload: payload.requestingOrgCode });
      }
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(updateMarginRecordFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Margin Record");
    yield put(updateMarginRecordFailure(errorMessage));
  }
}

// 🗑️ Worker: Delete Margin Record
function* deleteMarginRecordSaga({ payload: marginId }) {
  try {
    console.log("Saga: Calling deleteMarginRecord with marginId =", marginId);
    const response = yield call(deleteMarginRecordApi, marginId);
    console.log("Saga: Delete Margin Record Response =", response);

    if (response.status === 200) {
      // Remove status from response
      const { status, ...rest } = response;

      // If rest looks like a spread string (all numeric keys), rebuild the string
      const isStringObject =
        Object.keys(rest).every((key) => !isNaN(key)) &&
        Object.values(rest).every((val) => typeof val === "string");

      const finalResult = isStringObject ? Object.values(rest).join("") : rest;
      const successData = { message: finalResult };

      yield put(deleteMarginRecordSuccess(successData));
      // Note: We can't automatically refresh here since we don't have the requestingOrgCode
      // The component will need to handle this manually or we can store the current org in the state
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(deleteMarginRecordFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Delete Margin Record");
    yield put(deleteMarginRecordFailure(errorMessage));
  }
}

// 📊 Worker: Fetch Margin Summary
function* fetchMarginSummary() {
  try {
    console.log("Saga: Calling getMarginSummary");
    const response = yield call(getMarginSummaryApi);
    console.log("Saga: Margin Summary Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      // The response structure is { ...response.data, status: response.status }
      // Remove status from response to get the actual data
      const { status, ...marginSummaryData } = response;
      console.log("Saga: Margin Summary Data =", marginSummaryData);
      
      yield put(getMarginSummarySuccess(marginSummaryData));
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(getMarginSummaryFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Margin Summary");
    yield put(getMarginSummaryFailure(errorMessage));
  }
}

// 👂 Watchers
function* watchMargin() {
  yield takeLatest("margin/getMarginRecords", fetchMarginRecords);
  yield takeLatest("margin/createMarginRecord", createMarginRecordSaga);
  yield takeLatest("margin/updateMarginRecord", updateMarginRecordSaga);
  yield takeLatest("margin/deleteMarginRecord", deleteMarginRecordSaga);
  yield takeLatest("margin/getMarginSummary", fetchMarginSummary);
}

// 🧠 Root Saga
export function* marginSaga() {
  yield all([fork(watchMargin)]);
}

export default marginSaga;
